'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TbChevronLeft as ChevronLeft,
  TbChevronRight as ChevronRight,
  TbLink as LinkIcon,
  TbMaximize as Maximize2,
  TbPhoto as ImageIcon,
  TbPlayerPause as Pause,
  TbPlayerPlay as Play,
  TbVideo as VideoIcon,
  TbVolume2 as Volume2,
  TbVolumeOff as VolumeX,
  TbX as X,
  TbHeart as Heart,
  TbHeartFilled as HeartFilled,
  TbShare3 as Share,
  TbCheck as Check,
} from 'react-icons/tb';
import Link from 'next/link';
import { ServiceSticker } from '@/app/components/vectors/ServiceSticker';
import { useGalleryLikes } from './useGalleryLikes';
import { GestureHint, HeartBurst, useDoubleTap, useGestureHint, useHeartBurst } from './GalleryHeart';

interface GalleryItem {
  id: string | number;
  title: string;
  type: 'design' | 'video';
  image: {
    url: string;
    alt?: string;
    sizes?: {
      card?: { url: string };
      hero?: { url: string };
    };
  };
  videoUrl?: string;
  videoFile?: {
    url: string;
  };
  client?: string;
  description?: string;
  order?: number;
}

interface GalleryShowcaseProps {
  items: GalleryItem[];
  locale: string;
}

function BrandVectorIcon({ className = '' }: { className?: string }) {
  return (
    <ServiceSticker
      kind="strategy-brand-building"
      variant="orange"
      primary="#2283ba"
      secondary="#ffb53e"
      className={className}
    />
  );
}

// YouTube URL parser
/** Cards rendered per page. A multiple of 3 so the desktop grid never half-fills a row. */
const PAGE_SIZE = 12;

function getYouTubeId(url?: string) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

/** 1240 -> "1.2K", matching the compact counts Instagram shows under a reel. */
function formatLikes(count: number) {
  if (count < 1000) return String(count);
  if (count < 1_000_000) return `${(count / 1000).toFixed(count < 10_000 ? 1 : 0).replace(/\.0$/, '')}K`;
  return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

/** Share control. Native sheet where available, clipboard everywhere else. */
function ModalShareButton({ url, title, t }: { url: string; title: string; t: any }) {
  const [copied, setCopied] = useState(false);

  const share = async () => {
    // navigator.share needs a user gesture and a secure context; it rejects on
    // cancel too, which is not an error worth surfacing.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard can be blocked by permissions policy — nothing useful to do.
    }
  };

  return (
    <button
      onClick={share}
      aria-label={t.share}
      className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3 py-1 font-heading text-xs text-white transition-all duration-300 hover:scale-105 hover:border-white/30"
    >
      {copied ? <Check className="h-4 w-4 text-brand-cta" /> : <Share className="h-4 w-4" />}
      <span className="hidden sm:inline">{copied ? t.copied : t.share}</span>
    </button>
  );
}

/** The heart in the reel/design modal. Toggles, unlike the double-tap gesture. */
function ModalLikeButton({
  liked,
  likes,
  onClick,
  t,
}: {
  liked: boolean;
  likes: number;
  onClick: () => void;
  t: any;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={liked}
      aria-label={liked ? t.unlike : t.like}
      className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3 py-1 font-heading text-xs text-white transition-all duration-300 hover:scale-105 hover:border-white/30"
    >
      <motion.span
        key={liked ? 'on' : 'off'}
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 14 }}
        className="flex"
      >
        {liked ? <HeartFilled className="w-4 h-4 text-[#ff3040]" /> : <Heart className="w-4 h-4" />}
      </motion.span>
      {likes > 0 && <span className="tabular-nums font-semibold">{formatLikes(likes)}</span>}
    </button>
  );
}

interface GalleryGridItemProps {
  item: GalleryItem;
  idx: number;
  locale: string;
  isAr: boolean;
  t: any;
  handleOpen: (idx: number) => void;
  likes: number;
  isLiked: boolean;
  onToggleLike: (id: number) => void;
}

function GalleryGridItem({
  item,
  idx,
  locale,
  isAr,
  t,
  handleOpen,
  likes,
  isLiked,
  onToggleLike,
}: GalleryGridItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  /* Sticky: once a card has been hovered its video stays mounted, so moving the
     cursor back and forth does not re-request the file. */
  const [hasLoadedVideo, setHasLoadedVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const imageUrl = item.image?.sizes?.card?.url || item.image?.url;
  const fullImageUrl = item.image?.url;

  // Get direct video URL for inline preview
  const directVideoUrl = item.type === 'video' && !getYouTubeId(item.videoUrl)
    ? (item.videoFile?.url || item.videoUrl)
    : null;

  useEffect(() => {
    if (isHovered && directVideoUrl) setHasLoadedVideo(true);
  }, [isHovered, directVideoUrl]);

  // Handle play/pause on hover
  useEffect(() => {
    if (item.type === 'video' && videoRef.current && directVideoUrl) {
      if (isHovered) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [isHovered, hasLoadedVideo, item.type, directVideoUrl]);

  return (
    /* No `layout` prop: framer-motion measures every element carrying one on each
       render, which at 100+ cards blocked the main thread long enough for the
       section to look frozen. A plain opacity fade costs nothing. */
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => handleOpen(idx)}
      className="group relative aspect-square rounded-none border-none bg-black cursor-pointer transition-all duration-500 overflow-hidden w-full"
    >
      {/* Background Cover Image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={item.title}
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out rounded-none ${
            isHovered 
              ? item.type === 'design' 
                ? 'blur-md scale-110 opacity-30' 
                : 'scale-105 opacity-40' 
              : 'opacity-100 group-hover:scale-105'
          }`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-white/5 text-white/20 rounded-none">
          <ImageIcon className="w-12 h-12" />
        </div>
      )}

      {/* Video Hover Player. Mounted only after the first hover: a <video> with a
          src fetches metadata immediately, so rendering one per card made every
          published reel start downloading on page load. With ~87 of them queued
          against a browser's ~6 connections per host, the poster images behind
          them never arrived and the whole section rendered blank. */}
      {item.type === 'video' && directVideoUrl && hasLoadedVideo && (
        <video
          ref={videoRef}
          /* Decorative: this is the silent hover preview behind the card. The
     poster image already carries the meaning and the card has its own
     accessible name, so screen readers should skip it. */
          aria-hidden="true"
          src={directVideoUrl}
          poster={imageUrl}
          preload="none"
          loop
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 rounded-none pointer-events-none ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}

      {/* Uncropped Design Overlay on Hover */}
      {item.type === 'design' && isHovered && fullImageUrl && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 w-full h-full p-4 flex items-center justify-center z-10 pointer-events-none"
        >
          <img
            src={fullImageUrl}
            alt={item.title}
            className="max-w-[90%] max-h-[75%] object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.8)]"
          />
        </motion.div>
      )}

      {/* Indicator Icon */}
      <div className={`absolute bottom-4 left-4 z-20 bg-black/40 backdrop-blur-md border border-white/10 p-2.5 rounded-full text-white/90 transition-opacity duration-300 ${
        isHovered ? 'opacity-0' : 'opacity-100'
      }`}>
        {item.type === 'video' ? (
          <Play className="w-4 h-4 fill-white" />
        ) : (
          <ImageIcon className="w-4 h-4" />
        )}
      </div>

      {/* Like control. A tap on the card opens the reel, so the grid gets an
          explicit button rather than double-tap — the first tap of a double would
          open the modal before the second landed. Double-tap lives in the modal,
          which is where Instagram puts it too. */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleLike(Number(item.id));
        }}
        aria-pressed={isLiked}
        aria-label={isLiked ? t.unlike : t.like}
        className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/40 px-3 py-2 text-white/90 backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-white/30"
      >
        <motion.span
          key={isLiked ? 'on' : 'off'}
          initial={{ scale: 0.6 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 15 }}
          className="flex"
        >
          {isLiked ? (
            <HeartFilled className="w-4 h-4 text-[#ff3040]" />
          ) : (
            <Heart className="w-4 h-4" />
          )}
        </motion.span>
        {likes > 0 && (
          <span className="font-heading text-xs font-semibold tabular-nums">{formatLikes(likes)}</span>
        )}
      </button>

      {/* Centered Premium Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-5 z-20 rounded-none">
        <div className="w-full flex justify-between items-start">
          <span className="px-3 py-1 bg-black/40 border border-white/10 text-brand-text font-heading text-[10px] font-semibold uppercase tracking-wider rounded-full">
            {item.client || t.visitClient}
          </span>
          {item.type === 'video' && (
            <span className="px-2 py-0.5 bg-brand-cta/20 border border-brand-cta/40 text-brand-cta font-heading text-[9px] font-bold uppercase tracking-wider rounded-full flex items-center gap-1">
              <Play className="w-2.5 h-2.5 fill-brand-cta" />
              <span>{directVideoUrl ? 'Preview' : 'Reel'}</span>
            </span>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(idx);
            }}
            className="w-11 h-11 bg-white/10 hover:bg-brand-cta border border-white/10 hover:border-brand-cta rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-lg hover:scale-110"
            title={t.zoomPrompt}
          >
            <Maximize2 className="w-4.5 h-4.5" />
          </button>
          
          <Link
            href={`/${locale}/work`}
            onClick={(e) => e.stopPropagation()}
            className="w-11 h-11 bg-white/10 hover:bg-brand-cta border border-white/10 hover:border-brand-cta rounded-full flex items-center justify-center text-white transition-all duration-300 shadow-lg hover:scale-110"
            title={t.detailsPrompt}
          >
            <LinkIcon className="w-4.5 h-4.5" />
          </Link>
        </div>

        <div className="text-center w-full px-1 transform translate-y-3 group-hover:translate-y-0 transition-transform duration-500 ease-out delay-75">
          <h3 className="text-xs md:text-sm font-bold text-white font-heading leading-tight line-clamp-1">
            {item.title}
          </h3>
        </div>
      </div>
    </motion.div>
  );
}

export default function GalleryShowcase({ items, locale }: GalleryShowcaseProps) {
  const isAr = locale === 'ar';
  const [filter, setFilter] = useState<'all' | 'design' | 'video'>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Filter items
  const filteredItems = items.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  // Counts are fetched for the whole collection, not just what is on screen, so
  // the modal can navigate past the loaded page without a per-item request.
  const { counts, liked, toggleLike } = useGalleryLikes(items.map((item) => Number(item.id)));
  const { burstKey, burst } = useHeartBurst();

  // A narrower filter can leave the count stranded past the end of the new list.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeIdx === null) return;
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight') {
        isAr ? handlePrev() : handleNext();
      }
      if (e.key === 'ArrowLeft') {
        isAr ? handleNext() : handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIdx, filteredItems, isAr]);

  const handleOpen = (idx: number) => {
    setActiveIdx(idx);
    setIsPlaying(true);
  };

  const handleClose = () => {
    setActiveIdx(null);
  };

  const handleNext = () => {
    if (activeIdx === null) return;
    const nextIdx = (activeIdx + 1) % filteredItems.length;
    setActiveIdx(nextIdx);
    setIsPlaying(true);
  };

  const handlePrev = () => {
    if (activeIdx === null) return;
    const prevIdx = (activeIdx - 1 + filteredItems.length) % filteredItems.length;
    setActiveIdx(prevIdx);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Keep video play status synced with active changes
  useEffect(() => {
    if (videoRef.current && activeIdx !== null) {
      videoRef.current.load();
      videoRef.current.muted = isMuted;
      if (isPlaying) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [activeIdx]);

  // Lock background body scroll and disable Lenis smooth scroll when modal is active
  useEffect(() => {
    if (activeIdx !== null) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Stop Lenis if instantiated
      const lenis = (window as any).__nebrasLenis;
      if (lenis) lenis.stop();

      return () => {
        document.body.style.overflow = originalOverflow;
        const lenisInstance = (window as any).__nebrasLenis;
        if (lenisInstance) lenisInstance.start();
      };
    }
  }, [activeIdx]);

  // Swipe & Scroll Navigation (Instagram Reels Style)
  const touchStartY = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const minSwipeDistance = 50;
  const lastScrollTime = useRef<number>(0);
  const scrollThrottle = 850; // time in ms to throttle scroll updates

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchEndY.current = e.touches[0].clientY; // initialize to prevent click misinterprets
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartY.current || !touchEndY.current) return;
    const distance = touchStartY.current - touchEndY.current;
    const isSwipeUp = distance > minSwipeDistance;
    const isSwipeDown = distance < -minSwipeDistance;

    if (isSwipeUp) {
      // Swipe Up -> Next
      handleNext();
    } else if (isSwipeDown) {
      // Swipe Down -> Prev
      handlePrev();
    }

    touchStartY.current = 0;
    touchEndY.current = 0;
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (activeIdx === null) return;
    const now = Date.now();
    if (now - lastScrollTime.current < scrollThrottle) return;

    if (Math.abs(e.deltaY) > 8) {
      if (e.deltaY > 0) {
        handleNext();
      } else {
        handlePrev();
      }
      lastScrollTime.current = now;
    }
  };

  // Labels dictionary
  const t = {
    en: {
      sectionTitle: "Showcase & Reels",
      sectionSubtitle: "Explore our latest designs and vertical video campaigns",
      filterAll: "All",
      filterDesigns: "Designs",
      filterVideos: "Videos & Reels",
      close: "Close",
      next: "Next",
      prev: "Previous",
      visitClient: "Client Partner",
      playPrompt: "Click to play",
      viewDesign: "View Design",
      zoomPrompt: "Zoom & View",
      detailsPrompt: "Project Details",
      loadMore: "Load more",
      like: "Like",
      unlike: "Unlike",
      doubleTapHint: "Double-tap to like",
      mute: "Mute",
      unmute: "Unmute",
      share: "Share",
      copied: "Link copied",
      hintSwipe: "Swipe for the next reel",
      hintDoubleTap: "Double-tap to like"
    },
    ar: {
      sectionTitle: "المعرض والريلز",
      sectionSubtitle: "استكشف أحدث تصاميمنا وحملات الفيديو العمودية",
      filterAll: "الكل",
      filterDesigns: "التصاميم",
      filterVideos: "الفيديوهات والريلز",
      close: "إغلاق",
      next: "التالي",
      prev: "السابق",
      visitClient: "شريك النجاح",
      playPrompt: "انقر للتشغيل",
      viewDesign: "عرض التصميم",
      zoomPrompt: "تكبير وعرض",
      detailsPrompt: "تفاصيل المشروع",
      loadMore: "عرض المزيد",
      like: "إعجاب",
      unlike: "إلغاء الإعجاب",
      doubleTapHint: "اضغط مرتين للإعجاب",
      mute: "كتم الصوت",
      unmute: "تشغيل الصوت",
      share: "مشاركة",
      copied: "تم نسخ الرابط",
      hintSwipe: "اسحب للريل التالي",
      hintDoubleTap: "اضغط مرتين للإعجاب"
    }
  }[isAr ? 'ar' : 'en'];

  const activeItem = activeIdx !== null ? filteredItems[activeIdx] : null;
  const activeId = activeItem ? Number(activeItem.id) : null;
  const activeLiked = activeId !== null && liked.has(activeId);

  /* Double-tap only ever likes — it never removes one. Instagram behaves the
     same way: the burst plays on an already-liked reel, but the like stands.
     Unliking is the heart button's job, where the intent is unambiguous. */
  const handleDoubleTapLike = useCallback(() => {
    if (activeId === null) return;
    burst();
    if (!liked.has(activeId)) toggleLike(activeId, true);
  }, [activeId, liked, toggleLike, burst]);

  /* Play/pause is routed through the same gesture handler as the like so the two
     interpretations of a tap are resolved in one place. Previously the <video>
     had its own onClick, so both clicks of a double-tap landed on it and the reel
     stuttered every time it was liked. */
  const doubleTapHandlers = useDoubleTap(handleDoubleTapLike, togglePlay);
  const showGestureHint = useGestureHint(activeItem !== null);

  const shareUrl = activeItem
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/${locale}/work?reel=${activeItem.id}`
    : '';

  // Open straight into a shared reel. Runs once: after that the visitor's own
  // navigation owns the modal.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    deepLinkHandled.current = true;

    const requested = new URLSearchParams(window.location.search).get('reel');
    if (!requested) return;

    const index = filteredItems.findIndex((item) => String(item.id) === requested);
    if (index === -1) return;

    // Make sure the target is actually rendered in the grid behind the modal.
    setVisibleCount((count) => Math.max(count, index + 1));
    setActiveIdx(index);
    setIsPlaying(true);
  }, [filteredItems]);

  /* Keep the address bar pointing at whatever is on screen so it can be copied or
     shared at any moment. replaceState, not the Next router — pushing a route
     change would re-run the ISR page and tear the modal down mid-view. */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    if (activeItem) url.searchParams.set('reel', String(activeItem.id));
    else url.searchParams.delete('reel');

    const next = `${url.pathname}${url.search}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(null, '', next);
    }
  }, [activeItem]);

  return (
    <div className="w-full mt-24 py-16 border-t border-white/5 relative">
      {/* Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-cta/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-accent/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Header Container (Keeps centered bounds) */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col items-center gap-8 mb-12 text-center">
          <div className="space-y-4 max-w-xl md:max-w-2xl mx-auto">
            <span className="text-brand-cta font-heading text-lg font-semibold uppercase tracking-wider block">
              {t.sectionTitle}
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white leading-tight">
              {t.sectionSubtitle}
            </h2>
          </div>

          {/* Compact Filter Text Option */}
          <div className="flex items-center bg-white/[0.03] border border-white/10 p-1 rounded-full backdrop-blur-md flex-shrink-0 whitespace-nowrap shadow-xl">
            {([
              { key: 'all', label: t.filterAll },
              { key: 'design', label: t.filterDesigns },
              { key: 'video', label: t.filterVideos }
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`relative px-4 py-1.5 rounded-full text-[11px] md:text-xs font-heading font-semibold uppercase tracking-wider transition-colors duration-300 whitespace-nowrap flex-shrink-0 ${
                  filter === tab.key ? 'text-brand-bg font-bold' : 'text-brand-text/70 hover:text-white'
                }`}
              >
                {filter === tab.key && (
                  <motion.span
                    layoutId="activeFilterPill"
                    className="absolute inset-0 bg-brand-text rounded-full shadow-md"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Immersive Gallery Grid - Full Viewport Width, Zero Gap, Zero Radius, 3 Cols Desktop, 1 Col Mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 w-full">
        <AnimatePresence>
          {visibleItems.map((item, idx) => (
            <GalleryGridItem
              key={item.id}
              item={item}
              idx={idx}
              locale={locale}
              isAr={isAr}
              t={t}
              handleOpen={handleOpen}
              likes={counts[String(item.id)] ?? 0}
              isLiked={liked.has(Number(item.id))}
              onToggleLike={toggleLike}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* The collection went from 10 items to 110 in one import. Mounting them all
          at once is what made this section feel stuck, so the grid grows on
          demand — the modal still navigates the full filtered set. */}
      {hasMore && (
        <div className="flex justify-center mt-12">
          <button
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            className="group px-10 py-4 bg-white/5 border border-white/10 hover:border-brand-cta hover:bg-brand-cta/10 rounded-full font-heading text-lg font-bold tracking-wide transition-all duration-300 flex items-center gap-3"
          >
            <span>{t.loadMore}</span>
            <span className="text-brand-text/50 text-sm font-normal">
              {visibleItems.length} / {filteredItems.length}
            </span>
          </button>
        </div>
      )}

      {/* FULL-SCREEN IMERSIVE PLAYER MODAL */}
      <AnimatePresence>
        {activeItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
            data-lenis-prevent
            className="fixed inset-0 z-[1500] bg-black/95 backdrop-blur-2xl flex items-center justify-center overflow-hidden select-none touch-none"
          >
            {/* Modal Closer Mask */}
            <div className="absolute inset-0" onClick={handleClose} />

            {/* Header / Actions.
                The counter sits centred rather than in a justify-between row with
                the X. That row put it on the flex's *start* side — which flips to
                the physical right in Arabic, dropping the X onto the physical left
                where the frame's volume button lives. Centring the counter and
                pinning the volume button to the logical `start` means the three
                controls can never share a corner in either reading direction. */}
            <div className="pointer-events-none absolute inset-x-4 top-4 z-30 flex items-center justify-center md:inset-x-6 md:top-6">
              <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/10 bg-black/50 px-3 py-1.5 font-heading text-[11px] text-white/95 backdrop-blur-md md:gap-3 md:px-4 md:py-2 md:text-sm">
                <BrandVectorIcon className="hidden h-4 w-4 shrink-0 overflow-hidden rounded-sm md:block md:h-5 md:w-5" />
                <span className="hidden md:inline">
                  {filter === 'all' ? t.filterAll : filter === 'design' ? t.filterDesigns : t.filterVideos}
                </span>
                <span className="hidden text-white/40 md:inline">|</span>
                <span className="tabular-nums">{activeIdx! + 1} / {filteredItems.length}</span>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="absolute end-4 top-4 z-30 rounded-full border border-white/10 bg-black/50 p-2.5 text-white shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-brand-cta hover:bg-brand-cta md:end-6 md:top-6 md:p-3"
              aria-label={t.close}
            >
              <X className="h-5 w-5 md:h-6 md:w-6" />
            </button>

            {/* Navigation Arrows (Desktop).
                The glyph always matches the direction it physically points, while
                the action follows the reading direction — so in Arabic the
                left-hand arrow advances. This mirrors handleKeyDown, which already
                flipped for isAr; the buttons never did, which is why the arrows
                felt backwards on /ar. */}
            <button
              onClick={isAr ? handleNext : handlePrev}
              className="absolute left-6 top-1/2 -translate-y-1/2 hidden md:flex bg-white/5 hover:bg-brand-cta border border-white/10 hover:border-brand-cta p-4 rounded-full text-white transition-all duration-300 hover:scale-110 shadow-xl z-20"
              aria-label={isAr ? t.next : t.prev}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <button
              onClick={isAr ? handlePrev : handleNext}
              className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:flex bg-white/5 hover:bg-brand-cta border border-white/10 hover:border-brand-cta p-4 rounded-full text-white transition-all duration-300 hover:scale-110 shadow-xl z-20"
              aria-label={isAr ? t.prev : t.next}
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Main Player Frame - Dynamic width and height depending on content type */}
            {activeItem.type === 'video' ? (
              <motion.div
                initial={{ scale: 0.9, y: 30, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 30, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                {...doubleTapHandlers}
                className="relative w-full overflow-hidden z-10 flex flex-col items-center justify-center select-none max-w-[420px] aspect-[9/16] h-[85vh] max-h-[800px] md:rounded-3xl border border-white/15 bg-brand-bg shadow-2xl"
              >
                <HeartBurst burstKey={burstKey} />
                <GestureHint
                  show={showGestureHint}
                  swipeLabel={t.hintSwipe}
                  doubleTapLabel={t.hintDoubleTap}
                />
                {/* Media Content */}
                <div className="absolute inset-0 w-full h-full bg-black flex items-center justify-center overflow-hidden">
                  {getYouTubeId(activeItem.videoUrl) ? (
                    // YouTube Embed Iframe
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(activeItem.videoUrl)}?autoplay=1&mute=${isMuted ? 1 : 0}&loop=1&playlist=${getYouTubeId(activeItem.videoUrl)}&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1&enablejsapi=1`}
                      className="w-full h-full object-cover pointer-events-auto"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      title={activeItem.title}
                    />
                  ) : (
                    // Standard HTML5 loop player
                    <>
                      {/* No onClick here: taps are owned by the frame's gesture
                          handler, which decides between play/pause and like. */}
                      {/* Content, not decoration — the visitor chose to open this
                          reel, so it needs an accessible name rather than
                          aria-hidden. Captions are burned into the video itself
                          (A-03), so no <track> is claimed here. */}
                      <video
                        ref={videoRef}
                        aria-label={activeItem.title}
                        src={activeItem.videoFile?.url || activeItem.videoUrl}
                        loop
                        playsInline
                        autoPlay
                        className="w-full h-full object-cover cursor-pointer"
                      />
                      
                      <AnimatePresence>
                        {!isPlaying && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.5 }}
                            onClick={togglePlay}
                            className="absolute inset-0 m-auto w-20 h-20 bg-black/55 backdrop-blur-sm rounded-full flex items-center justify-center text-white/90 border border-white/10 cursor-pointer shadow-2xl pointer-events-auto"
                          >
                            <Play className="w-10 h-10 fill-white" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Volume Toggle Trigger Overlay */}
                  <button
                    onClick={() => {
                      if (getYouTubeId(activeItem.videoUrl)) {
                        setIsMuted(!isMuted);
                      } else {
                        toggleMute();
                      }
                    }}
                    /* `start-4`, not `left-4`. The close button always lands on the
                       logical end, so pinning this to the logical start guarantees
                       they sit on opposite sides in both Arabic and English. */
                    className="pointer-events-auto absolute start-4 top-4 z-20 rounded-full border border-white/10 bg-black/60 p-2 text-white shadow-md transition-all duration-300 hover:scale-105 hover:bg-black/80 md:p-2.5"
                    aria-label={isMuted ? t.unmute : t.mute}
                  >
                    {isMuted ? (
                      <VolumeX className="h-4 w-4 text-brand-cta md:h-5 md:w-5" />
                    ) : (
                      <Volume2 className="h-4 w-4 md:h-5 md:w-5" />
                    )}
                  </button>
                </div>

                {/* Glassmorphic Description Overlay */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-6 pt-16 flex flex-col justify-end text-white select-text pointer-events-auto z-20">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-brand-cta/25 border border-brand-cta/40 text-brand-cta font-heading text-xs font-semibold rounded-full uppercase tracking-wider block">
                      {activeItem.client || t.visitClient}
                    </span>
                    <span className="px-3 py-1 bg-white/10 border border-white/10 text-white font-heading text-xs rounded-full flex items-center gap-1">
                      <VideoIcon className="w-3 h-3 text-brand-cta" />
                      <span>{getYouTubeId(activeItem.videoUrl) ? 'YouTube' : 'Reel'}</span>
                    </span>

                    <div className="ms-auto flex items-center gap-2">
                      <ModalShareButton url={shareUrl} title={activeItem.title} t={t} />
                      <ModalLikeButton
                        liked={activeLiked}
                        likes={counts[String(activeItem.id)] ?? 0}
                        onClick={() => {
                          if (activeId === null) return;
                          if (!activeLiked) burst();
                          toggleLike(activeId);
                        }}
                        t={t}
                      />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold font-heading leading-tight mb-2 tracking-wide">
                    {activeItem.title}
                  </h3>

                  {activeItem.description && (
                    <p className="text-sm text-brand-text/80 leading-relaxed font-heading line-clamp-3 overflow-y-auto max-h-[90px] pr-2">
                      {activeItem.description}
                    </p>
                  )}

                  {/* Mobile Navigation controls */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-brand-text/50 font-heading select-none pointer-events-none">
                    <span>{t.doubleTapHint}</span>
                    {/* dir="ltr" pins the physical order of this pair.
                        Without it RTL reverses the flex children, so the
                        ChevronLeft glyph lands on the physical right — and the
                        locale flip below then cancels out, which is why the
                        Arabic arrows still read backwards after the first fix.
                        The desktop arrows above never had this problem because
                        they are absolutely positioned. */}
                    <div dir="ltr" className="flex gap-2 pointer-events-auto">
                      <button
                        onClick={isAr ? handleNext : handlePrev}
                        className="p-1 hover:text-white transition-colors"
                        aria-label={isAr ? t.next : t.prev}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={isAr ? handlePrev : handleNext}
                        className="p-1 hover:text-white transition-colors"
                        aria-label={isAr ? t.prev : t.next}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0.9, y: 30, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 30, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                className="relative z-10 flex flex-col items-center justify-center max-w-[95vw] md:max-w-6xl select-none"
              >
                {/* Image Frame */}
                <div
                  {...doubleTapHandlers}
                  className="relative flex items-center justify-center max-h-[78vh] w-full overflow-visible border border-white/10 shadow-2xl bg-black/45 backdrop-blur-md"
                >
                  <HeartBurst burstKey={burstKey} />
                  <img
                    src={activeItem.image?.url}
                    alt={activeItem.title}
                    className="max-w-full max-h-[78vh] w-auto h-auto object-contain select-text"
                  />
                  
                  {/* Image Indicator Badge */}
                  <div className="absolute top-4 left-4 bg-black/60 border border-white/10 p-2.5 rounded-full text-white z-20 backdrop-blur-md">
                    <ImageIcon className="w-5 h-5 text-brand-text" />
                  </div>
                </div>

                {/* Elegant Caption Card under the image */}
                <div className="w-full max-w-6xl mt-3 bg-black/65 border border-white/10 p-5 backdrop-blur-md text-white select-text pointer-events-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-brand-cta/25 border border-brand-cta/40 text-brand-cta font-heading text-xs font-semibold rounded-full uppercase tracking-wider block">
                      {activeItem.client || t.visitClient}
                    </span>
                    <span className="px-3 py-1 bg-white/10 border border-white/10 text-white font-heading text-xs rounded-full flex items-center gap-1">
                      <ImageIcon className="w-3 h-3 text-brand-cta" />
                      <span>{t.filterDesigns}</span>
                    </span>

                    <div className="ms-auto flex items-center gap-2">
                      <ModalShareButton url={shareUrl} title={activeItem.title} t={t} />
                      <ModalLikeButton
                        liked={activeLiked}
                        likes={counts[String(activeItem.id)] ?? 0}
                        onClick={() => {
                          if (activeId === null) return;
                          if (!activeLiked) burst();
                          toggleLike(activeId);
                        }}
                        t={t}
                      />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold font-heading leading-tight mb-2 tracking-wide text-white">
                    {activeItem.title}
                  </h3>

                  {activeItem.description && (
                    <p className="text-sm text-brand-text/80 leading-relaxed font-heading overflow-y-auto max-h-[90px] pr-2">
                      {activeItem.description}
                    </p>
                  )}

                  {/* Mobile Navigation controls */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-brand-text/50 font-heading select-none pointer-events-none">
                    <span>{t.doubleTapHint}</span>
                    {/* dir="ltr" pins the physical order of this pair.
                        Without it RTL reverses the flex children, so the
                        ChevronLeft glyph lands on the physical right — and the
                        locale flip below then cancels out, which is why the
                        Arabic arrows still read backwards after the first fix.
                        The desktop arrows above never had this problem because
                        they are absolutely positioned. */}
                    <div dir="ltr" className="flex gap-2 pointer-events-auto">
                      <button
                        onClick={isAr ? handleNext : handlePrev}
                        className="p-1 hover:text-white transition-colors"
                        aria-label={isAr ? t.next : t.prev}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={isAr ? handlePrev : handleNext}
                        className="p-1 hover:text-white transition-colors"
                        aria-label={isAr ? t.prev : t.next}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
